import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import { 
  Check, 
  Zap, 
  Crown,
  Users,
  Loader2,
  CreditCard,
  ArrowRight,
  Clock,
  Sparkles,
  X,
  AlertCircle,
  Infinity
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const planIcons = {
  pro: Zap,
  enterprise: Crown
};

export default function BillingPage() {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState(5);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchPlans();
    fetchSubscription();
    
    // Check for success redirect
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API}/billing/plans`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
      }
    } catch (error) {
      console.error("Failed to fetch plans:", error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await fetch(`${API}/billing/subscription`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Payment status check timed out. Please refresh the page.");
      return;
    }

    try {
      const response = await fetch(`${API}/billing/checkout/status/${sessionId}`, {
        credentials: "include"
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === "paid") {
          toast.success("Payment successful! Your plan has been upgraded.");
          window.history.replaceState({}, document.title, window.location.pathname);
          fetchSubscription();
          return;
        } else if (data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          return;
        }
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Poll error:", error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handleStartTrial = async (planId) => {
    setStartingTrial(true);
    try {
      const response = await fetch(`${API}/billing/start-trial`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          plan_id: planId,
          seats: 1
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Trial started! You have ${data.trial_days} days to explore.`);
        fetchSubscription();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start trial");
      }
    } catch (error) {
      toast.error("Failed to start trial");
    } finally {
      setStartingTrial(false);
    }
  };

  const handleCheckout = async (planId) => {
    setCheckingOut(true);
    setSelectedPlan(planId);

    try {
      const response = await fetch(`${API}/billing/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          plan_id: planId,
          seats: selectedSeats,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create checkout");
        setCheckingOut(false);
        setSelectedPlan(null);
      }
    } catch (error) {
      toast.error("Failed to create checkout");
      setCheckingOut(false);
      setSelectedPlan(null);
    }
  };

  const calculatePrice = (plan, seats) => {
    let pricePerSeat = plan.price_per_seat;
    const teamDiscounts = plan.team_discounts || {};
    
    // Apply team discounts
    for (const [minSeats, discountedPrice] of Object.entries(teamDiscounts).sort((a, b) => parseInt(b[0]) - parseInt(a[0]))) {
      if (seats >= parseInt(minSeats)) {
        pricePerSeat = discountedPrice;
        break;
      }
    }
    
    return {
      perSeat: pricePerSeat,
      total: pricePerSeat * seats,
      savings: Math.round((1 - pricePerSeat / plan.price_per_seat) * 100)
    };
  };

  const formatLeadLimit = (limit) => {
    if (limit === -1) return "Unlimited";
    return `${limit}/day`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="billing-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const currentPlan = subscription?.plan;
  const isOnTrial = subscription?.trial_info?.is_trial;
  const trialDaysLeft = subscription?.trial_info?.days_left;

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="billing-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
          Billing & Plans
        </h1>
        <p className="text-slate-500 mt-1">
          Choose the plan that best fits your team&apos;s needs
        </p>
      </div>

      {/* Trial Banner */}
      {isOnTrial && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4 flex items-center gap-4">
            <div className="p-2 bg-amber-100 rounded-full">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-amber-900">
                You&apos;re on a free trial of the {subscription?.plan_name || "Pro"} plan
              </p>
              <p className="text-sm text-amber-700">
                {trialDaysLeft} days remaining. Add payment to continue after trial ends.
              </p>
            </div>
            <Button 
              onClick={() => handleCheckout(currentPlan)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Add Payment Method
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Current Plan */}
      {currentPlan && currentPlan !== "trial" && !isOnTrial && (
        <Card data-testid="current-plan">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              Current Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-slate-900 capitalize">
                  {subscription?.plan_name || currentPlan}
                </p>
                <p className="text-slate-500">
                  {subscription?.seats_purchased || 1} seats • 
                  {subscription?.limits?.daily_leads === -1 ? " Unlimited" : ` ${subscription?.limits?.daily_leads || 100}`} leads/day per user
                </p>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Seat Selection */}
      <Card data-testid="seat-selection">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Team Size
          </CardTitle>
          <CardDescription>
            Select the number of seats for your team. Get discounts for larger teams!
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Slider
              value={[selectedSeats]}
              onValueChange={(value) => setSelectedSeats(value[0])}
              min={1}
              max={100}
              step={1}
              className="flex-1"
            />
            <div className="w-20 text-center">
              <span className="text-2xl font-bold text-primary">{selectedSeats}</span>
              <span className="text-sm text-slate-500 block">seats</span>
            </div>
          </div>
          
          {/* Quick select buttons */}
          <div className="flex gap-2 flex-wrap">
            {[1, 5, 10, 25, 50].map((num) => (
              <Button
                key={num}
                variant={selectedSeats === num ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedSeats(num)}
              >
                {num} {num === 1 ? "seat" : "seats"}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-2 gap-6" data-testid="plans-grid">
        {plans.map((plan) => {
          const Icon = planIcons[plan.plan_id] || Zap;
          const pricing = calculatePrice(plan, selectedSeats);
          const isCurrentPlan = currentPlan === plan.plan_id;
          
          return (
            <Card 
              key={plan.plan_id}
              className={`relative overflow-hidden transition-all ${
                plan.is_popular ? "ring-2 ring-primary shadow-lg" : ""
              } ${isCurrentPlan ? "bg-primary/5" : ""}`}
              data-testid={`plan-${plan.plan_id}`}
            >
              {plan.is_popular && (
                <div className="absolute top-0 right-0 bg-primary text-white text-xs font-medium px-3 py-1 rounded-bl-lg">
                  Most Popular
                </div>
              )}
              
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${plan.is_popular ? "bg-primary text-white" : "bg-slate-100"}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{plan.name}</CardTitle>
                      <CardDescription>{plan.description}</CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Pricing */}
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold">${pricing.perSeat.toFixed(0)}</span>
                    <span className="text-slate-500">/seat/month</span>
                  </div>
                  {selectedSeats > 1 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-lg font-semibold text-slate-700">
                        Total: ${pricing.total.toFixed(0)}/month for {selectedSeats} seats
                      </p>
                      {pricing.savings > 0 && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          Save {pricing.savings}% with team pricing
                        </Badge>
                      )}
                    </div>
                  )}
                </div>

                {/* Trial Info */}
                {plan.trial_days > 0 && !isCurrentPlan && (
                  <div className="flex items-center gap-2 text-sm text-primary">
                    <Sparkles className="w-4 h-4" />
                    <span>{plan.trial_days}-day free trial available</span>
                  </div>
                )}

                {/* Features */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    {plan.daily_leads === -1 ? (
                      <Infinity className="w-4 h-4 text-primary" />
                    ) : (
                      <Check className="w-4 h-4 text-emerald-500" />
                    )}
                    <span className="font-medium">
                      {formatLeadLimit(plan.daily_leads)} leads per user
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-emerald-500" />
                    <span>Up to {plan.max_seats} team members</span>
                  </div>
                  {plan.min_seats > 1 && (
                    <div className="flex items-center gap-2 text-sm text-amber-600">
                      <Check className="w-4 h-4 text-amber-500" />
                      <span>Minimum {plan.min_seats} seats required</span>
                    </div>
                  )}
                  {plan.features?.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="space-y-2 pt-4">
                  {isCurrentPlan ? (
                    <Button disabled className="w-full" variant="outline">
                      Current Plan
                    </Button>
                  ) : (
                    <>
                      <Button
                        className="w-full"
                        onClick={() => handleCheckout(plan.plan_id)}
                        disabled={checkingOut || (plan.min_seats > 1 && selectedSeats < plan.min_seats)}
                        data-testid={`checkout-${plan.plan_id}`}
                      >
                        {checkingOut && selectedPlan === plan.plan_id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CreditCard className="w-4 h-4 mr-2" />
                        )}
                        {plan.min_seats > 1 && selectedSeats < plan.min_seats
                          ? `Minimum ${plan.min_seats} seats`
                          : "Subscribe Now"}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                      
                      {plan.trial_days > 0 && !subscription?.trial_info?.is_trial && (
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => handleStartTrial(plan.plan_id)}
                          disabled={startingTrial}
                          data-testid={`trial-${plan.plan_id}`}
                        >
                          {startingTrial ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Clock className="w-4 h-4 mr-2" />
                          )}
                          Start {plan.trial_days}-Day Free Trial
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* No Plans Message */}
      {plans.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="font-medium text-slate-900 mb-2">No Plans Available</h3>
            <p className="text-slate-500">
              Please contact the administrator to set up subscription plans.
            </p>
          </CardContent>
        </Card>
      )}

      {/* FAQ or Additional Info */}
      <Card className="bg-slate-50">
        <CardContent className="py-6">
          <h3 className="font-semibold mb-4">Frequently Asked Questions</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium text-slate-900">Can I cancel anytime?</p>
              <p className="text-slate-500">Yes, you can cancel your subscription at any time.</p>
            </div>
            <div>
              <p className="font-medium text-slate-900">What happens after my trial?</p>
              <p className="text-slate-500">Add a payment method to continue, or your account will revert to limited access.</p>
            </div>
            <div>
              <p className="font-medium text-slate-900">Can I change my plan later?</p>
              <p className="text-slate-500">Yes, you can upgrade or downgrade at any time.</p>
            </div>
            <div>
              <p className="font-medium text-slate-900">Do you offer refunds?</p>
              <p className="text-slate-500">We offer a prorated refund if you downgrade mid-cycle.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
